import { ThemedText } from "@/components/themed-text";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";

type StatusVariant = "success" | "danger" | "warning" | "neutral";

const VARIANT_COLORS: Record<StatusVariant, string> = {
  success: "#1F7A4C",
  danger: "#C94C4C",
  warning: "#D97706",
  neutral: "#6B7280",
};

export function formatStatusDate(dateStr: string): string {
  const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const date = slashMatch
    ? new Date(
        Number(slashMatch[3]),
        Number(slashMatch[2]) - 1,
        Number(slashMatch[1]),
      )
    : new Date(dateStr);

  if (Number.isNaN(date.getTime())) {
    return dateStr;
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function isValidVehicleStatus(status?: string | null): boolean {
  const normalized = status?.toLowerCase();
  return normalized === "valid" || normalized === "taxed";
}

function normalizeStatus(status?: string | null): string {
  return status?.trim().toLowerCase() ?? "";
}

export function getMotBadge(
  status?: string | null,
  expiryDate?: string | null,
): {
  label: string;
  message: string;
  variant: StatusVariant;
} | null {
  const normalized = normalizeStatus(status);

  if (!normalized && !expiryDate) {
    return null;
  }

  if (normalized === "valid") {
    return {
      label: "MOT current",
      message: expiryDate
        ? `MOT current until ${formatStatusDate(expiryDate)}`
        : "MOT current",
      variant: "success",
    };
  }

  if (normalized === "failed") {
    return {
      label: "MOT failed",
      message: "MOT failed - check before arranging transport",
      variant: "danger",
    };
  }

  if (normalized === "expired" || normalized === "invalid") {
    return {
      label: "MOT expired",
      message: expiryDate
        ? `Expired: ${formatStatusDate(expiryDate)} - do not drive unless legally exempt`
        : "MOT expired - do not drive unless legally exempt",
      variant: "danger",
    };
  }

  return {
    label: "Unknown",
    message: "Status unavailable - try again later",
    variant: "neutral",
  };
}

export function getTaxBadge(
  status?: string | null,
  dueDate?: string | null,
): {
  label: string;
  message: string;
  variant: StatusVariant;
} | null {
  const normalized = normalizeStatus(status);

  if (!normalized && !dueDate) {
    return null;
  }

  if (normalized === "taxed") {
    return {
      label: "Taxed",
      message: dueDate ? `Taxed until ${formatStatusDate(dueDate)}` : "Taxed",
      variant: "success",
    };
  }

  if (normalized === "sorn") {
    return {
      label: "SORN",
      message: "SORN - off road",
      variant: "warning",
    };
  }

  if (
    normalized === "expired" ||
    normalized === "untaxed" ||
    normalized === "invalid"
  ) {
    return {
      label: "Untaxed",
      message: dueDate
        ? `Vehicle tax not current - expired ${formatStatusDate(dueDate)}`
        : "Vehicle tax not current",
      variant: "danger",
    };
  }

  return {
    label: "Unknown",
    message: "Status unavailable - try again later",
    variant: "neutral",
  };
}

export interface VehicleStatusBoxProps {
  label: string;
  message: string;
  variant: StatusVariant;
}

export function VehicleStatusBox({
  label,
  message,
  variant,
}: VehicleStatusBoxProps) {
  const iconName =
    variant === "success"
      ? "checkmark"
      : variant === "warning"
        ? "alert"
        : variant === "neutral"
          ? "help"
          : "close";

  return (
    <View
      style={[styles.statusBox, { backgroundColor: VARIANT_COLORS[variant] }]}
    >
      <Ionicons
        name={iconName}
        size={20}
        color="#fff"
        style={styles.statusBoxIcon}
      />
      <View style={styles.statusBoxText}>
        {label ? (
          <ThemedText style={styles.statusBoxLabel} type="smallSemiBold">
            {label}
          </ThemedText>
        ) : null}
        <ThemedText style={styles.statusBoxMessage} type="extraSmall">
          {message}
        </ThemedText>
      </View>
    </View>
  );
}

export interface VehicleStatusRowProps {
  motStatus?: string | null;
  motExpiryDate?: string | null;
  taxStatus?: string | null;
  taxDueDate?: string | null;
  style?: StyleProp<ViewStyle>;
}

export function VehicleStatusRow({
  motStatus,
  motExpiryDate,
  taxStatus,
  taxDueDate,
  style,
}: VehicleStatusRowProps) {
  const taxBadge = getTaxBadge(taxStatus, taxDueDate);
  const motBadge = getMotBadge(motStatus, motExpiryDate);
  const unknownBadge = {
    label: "Unknown",
    message: "Status unavailable - try again later",
    variant: "neutral",
  };

  if (!taxBadge && !motBadge) {
    return null;
  }

  return (
    <View style={[styles.statusRow, style]}>
      {taxBadge ? (
        <VehicleStatusBox
          label={taxBadge.label}
          message={taxBadge.message}
          variant={taxBadge.variant}
        />
      ) : (
        <VehicleStatusBox
          label={"Tax Unknown"}
          message={unknownBadge.message}
          variant={unknownBadge.variant as StatusVariant}
        />
      )}
      {motBadge ? (
        <VehicleStatusBox
          label={motBadge.label}
          message={motBadge.message}
          variant={motBadge.variant}
        />
      ) : (
        <VehicleStatusBox
          label={"MOT Unknown"}
          message={unknownBadge.message}
          variant={unknownBadge.variant as StatusVariant}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  statusRow: {
    flexDirection: "row",
    gap: 8,
  },
  statusBox: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRadius: 10,
    gap: 8,
  },
  statusBoxIcon: {
    flexShrink: 0,
  },
  statusBoxText: {
    flexShrink: 1,
    alignItems: "center",
  },
  statusBoxLabel: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "700",
    textAlign: "center",
  },
  statusBoxMessage: {
    color: "#fff",
    fontSize: 11,
    fontWeight: "500",
    textAlign: "center",
    marginTop: 2,
    opacity: 0.95,
    lineHeight: 15,
  },
});
